import { useCallback, useEffect, useState } from 'react'
import { adminApi } from '../../services/api'
import { useSocket } from '../../hooks/useSocket'
import { openServiceLabels } from './printDocs'

type GroupLabel = {
  group_id: string
  code?: string | null
  label: string
  service_slug?: string
  delivery_model?: string
  item_count: number
  status: string
  qr_token: string
  qr_data_url: string | null
  items?: {
    name: string
    /** Parent service ("Wash & Fold"), so a line never reads as a bare pricing model. */
    service_name?: string | null
    quantity: number
    item_count?: number | null
    unit?: string | null
    unit_price?: number
    total_price?: number
  }[]
  display_id?: string
  order_number?: string
  customer_name?: string | null
}

type Props = { orderId: string; displayId?: string }

const STATUS_LABEL: Record<string, string> = {
  received: 'Received', washing: 'Washing', washed: 'Washed', ironing: 'Ironing',
  ironed: 'Ironed', qc: 'Quality check', packed: 'Packed', ready: 'Ready',
  qc_failed: 'Rechecking', on_hold: 'On hold',
  // legacy coarse
  at_facility: 'Received', in_wash: 'Washing', in_iron: 'Ironing', quality_check: 'Quality check',
  failed: 'Failed', cancelled: 'Cancelled', returned: 'Returned',
}

const statusColor = (status: string): { bg: string; fg: string } => {
  if (status === 'ready') return { bg: '#EAF8EE', fg: '#15803D' }
  if (['failed', 'cancelled', 'returned'].includes(status)) return { bg: '#FEF2F2', fg: '#B91C1C' }
  return { bg: '#EAF1FA', fg: '#1D4ED8' }
}

const unwrap = (res: any) => res?.data ?? res

const formatMoney = (value: number) =>
  `₹${Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

/**
 * A line stated in full: quantity, the rate it was priced at, and the amount.
 * "By weight" on its own names a pricing model, not what was sold - and without
 * the rate the admin cannot check the amount against anything.
 *
 * The rate is only shown when the order actually carries one; it is never
 * reconstructed by dividing, which would invent a unit price for a line that
 * was priced some other way.
 */
const describeLine = (it: {
  name: string
  service_name?: string | null
  quantity: number
  unit?: string | null
  unit_price?: number
}): string => {
  const perKg = String(it.unit || '').toLowerCase() === 'per_kg'
  const qty = `${it.quantity}${perKg ? ' kg' : ' ×'}`
  // The service is already the group heading; repeat it on the line only when
  // it differs, so a single-service group does not say the same word twice.
  const label = it.service_name && it.service_name !== it.name ? `${it.service_name}. ${it.name}` : it.name
  const rate =
    Number(it.unit_price) > 0 ? ` @ ${formatMoney(Number(it.unit_price))}${perKg ? '/kg' : ' each'}` : ''
  return `${qty} ${label}${rate}`
}

// One sticker per service bag (code, service, items, QR) via the shared printer.
function printLabels(labels: GroupLabel[]) {
  const first = labels[0]
  openServiceLabels(
    {
      orderNumber: first?.order_number || '',
      displayId: first?.display_id,
      customerName: first?.customer_name || 'Customer',
    },
    labels.map((l) => ({
      group_id: l.group_id,
      code: l.code,
      label: l.label,
      service_slug: l.service_slug,
      delivery_model: l.delivery_model,
      item_count: l.item_count,
      qr_data_url: l.qr_data_url,
      items: l.items || [],
    })),
  )
}

export default function OrderGroupsSection({ orderId, displayId }: Props) {
  const [groups, setGroups] = useState<GroupLabel[]>([])
  const [enabled, setEnabled] = useState(true)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      const payload = unwrap(await adminApi.getOrderGroupLabels(orderId))
      setEnabled(payload?.enabled !== false)
      setGroups(Array.isArray(payload?.labels) ? payload.labels : [])
    } catch {
      setError('Unable to load service groups.')
    } finally {
      setLoading(false)
    }
  }, [orderId])

  useEffect(() => { void load() }, [load])

  // Live: reload when any group in this order advances (scan or other operator).
  useSocket({
    'order:group_updated': (payload: Record<string, unknown>) => {
      if (payload?.order_id === orderId) void load()
    },
  })

  const override = async (groupId: string, action: 'advance' | 'fail') => {
    if (action === 'fail' && !window.confirm('Mark this service group as failed? The order will be held for review.')) return
    setBusyId(groupId)
    setError('')
    try {
      await adminApi.overrideOrderGroup(orderId, groupId, action)
      await load()
    } catch {
      setError(`Could not ${action} the group. It may have changed, so it was reloaded.`)
      await load()
    } finally {
      setBusyId(null)
    }
  }

  // Feature off or single-service order => nothing to show (parity with today).
  if (!loading && (!enabled || groups.length < 2)) return null

  return (
    <div style={{ marginTop: 16, border: '1px solid #E8E2D4', borderRadius: 12, padding: 12, background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h4 style={{ margin: 0, color: '#0F2E2A' }}>Service Groups{displayId ? ` · ${displayId}` : ''}</h4>
        <button
          type="button"
          onClick={() => printLabels(groups)}
          disabled={loading || groups.length === 0}
          style={{ border: '1px solid #0F2E2A', background: '#fff', color: '#0F2E2A', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 13 }}
        >
          Print group labels
        </button>
      </div>

      {error ? <div style={{ color: '#B91C1C', fontSize: 13, marginBottom: 8 }}>{error}</div> : null}
      {loading ? <div style={{ color: '#888', fontSize: 13 }}>Loading groups…</div> : null}

      {groups.map((g) => {
        const c = statusColor(g.status)
        const terminal = ['ready', 'failed', 'cancelled', 'returned'].includes(g.status)
        return (
          <div key={g.group_id} style={{ padding: '10px 0', borderTop: '1px solid #F2EEE3' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#0F2E2A', fontWeight: 600 }}>
                  {g.code ? <span style={{ background: '#D6B97B', color: '#0F2E2A', borderRadius: 6, padding: '1px 6px', fontSize: 12, marginRight: 6 }}>{g.code}</span> : null}
                  {g.label}{String(g.delivery_model).toLowerCase() === 'express' ? ' ⚡' : ''}
                </div>
                <div style={{ color: '#777', fontSize: 12 }}>{g.item_count} item{g.item_count === 1 ? '' : 's'}</div>
              </div>
              <span style={{ background: c.bg, color: c.fg, borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 600, marginRight: 10 }}>
                {STATUS_LABEL[g.status] || g.status}
              </span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  type="button"
                  onClick={() => override(g.group_id, 'advance')}
                  disabled={busyId === g.group_id || terminal}
                  style={{ border: 0, borderRadius: 8, padding: '6px 10px', cursor: terminal ? 'not-allowed' : 'pointer', fontSize: 13, background: terminal ? '#E5E7EB' : '#D6B97B', color: '#0F2E2A', opacity: busyId === g.group_id ? 0.6 : 1 }}
                >
                  {busyId === g.group_id ? '…' : 'Advance'}
                </button>
                <button
                  type="button"
                  onClick={() => override(g.group_id, 'fail')}
                  disabled={busyId === g.group_id || g.status === 'failed'}
                  style={{ border: '1px solid #FCA5A5', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 13, background: '#fff', color: '#B91C1C', opacity: g.status === 'failed' ? 0.5 : 1 }}
                >
                  Fail
                </button>
              </div>
            </div>
            {g.items && g.items.length > 0 ? (
              <ul style={{ margin: '6px 0 0', paddingLeft: 18, color: '#555', fontSize: 12.5 }}>
                {g.items.map((it, i) => (
                  <li key={`${g.group_id}-${i}`}>
                    {describeLine(it)}
                    {Number(it.item_count) > 0 ? ` · ${it.item_count} items` : ''}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}
